// EOL/EOS CSV Upload Panel - Bulk import product data from vendor CSV files

import React, { useState, useRef } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  Dropdown,
  DetailsList,
  SelectionMode,
  useTheme,
} from '@fluentui/react';
import type { IDropdownOption, IColumn } from '@fluentui/react';
import { useEolEos } from '../contexts';
import type { EndOfLifeProduct, VendorType } from '../types';

const vendorOptions: IDropdownOption[] = [
  { key: 'Aruba', text: 'Aruba' },
  { key: 'Extreme', text: 'Extreme Networks' },
  { key: 'Fortinet', text: 'Fortinet' },
  { key: 'APC', text: 'APC' },
  { key: 'Meraki', text: 'Meraki' },
  { key: 'PaloAlto', text: 'Palo Alto' },
  { key: 'Sonicwall', text: 'Sonicwall' },
];

export const CsvUploadPanel: React.FC = () => {
  const theme = useTheme();
  const { uploadFile, saveProducts, isLoading, error } = useEolEos();
  const [vendorType, setVendorType] = useState<VendorType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedProducts, setParsedProducts] = useState<EndOfLifeProduct[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleVendorChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setVendorType(option.key as VendorType);
      setSuccessMessage('');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setFile(files[0]);
      setSuccessMessage('');
      setParsedProducts([]);
    }
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!file || !vendorType) {
      return;
    }

    setUploadProgress(0);
    setParsedProducts([]);
    setSuccessMessage('');

    const products = await uploadFile(file, vendorType, (progress) => {
      setUploadProgress(progress);
    });

    if (products) {
      setParsedProducts(products);
      setSuccessMessage(
        `Successfully parsed ${products.length} product(s) from the ${vendorType} file. Review and click "Save to Database" to save them.`
      );
      setUploadProgress(0);
    }
  };

  const handleSaveProducts = async () => {
    if (parsedProducts.length === 0) {
      return;
    }

    const result = await saveProducts(parsedProducts);
    if (result) {
      setSuccessMessage('Products successfully saved to database!');
      setParsedProducts([]);
      setFile(null);
      setVendorType(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const columns: IColumn[] = [
    {
      key: 'manufactuerName',
      name: 'Manufacturer',
      fieldName: 'manufactuerName',
      minWidth: 120,
      maxWidth: 150,
    },
    {
      key: 'productName',
      name: 'Product Name',
      fieldName: 'productName',
      minWidth: 150,
      maxWidth: 250,
      isMultiline: true,
    },
    {
      key: 'productNumber',
      name: 'Product #',
      fieldName: 'productNumber',
      minWidth: 100,
      maxWidth: 130,
    },
    {
      key: 'endOfSaleDate',
      name: 'EOL Date',
      fieldName: 'endOfSaleDate',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: EndOfLifeProduct) => (
        <Text>{item.endOfSaleDate || '-'}</Text>
      ),
    },
    {
      key: 'endOfSupportDate',
      name: 'EOS Date',
      fieldName: 'endOfSupportDate',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: EndOfLifeProduct) => (
        <Text>{item.endOfSupportDate || '-'}</Text>
      ),
    },
  ];

  const displayedProducts = parsedProducts.slice(0, 20);
  const remainingCount = parsedProducts.length - displayedProducts.length;

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Text variant="xLarge">Upload EOL Data</Text>

      <Text>
        Select a vendor type and upload a CSV file containing product lifecycle data. 
        The file will be parsed according to the vendor's format, and you can review the results before saving.
      </Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => {}}>
          {error}
        </MessageBar>
      )}

      {successMessage && (
        <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccessMessage('')}>
          {successMessage}
        </MessageBar>
      )}

      <Stack horizontal tokens={{ childrenGap: 30 }} styles={{ root: { alignItems: 'flex-start' } }}>
        <Stack.Item grow={1}>
          <Stack tokens={{ childrenGap: 16 }}>
            <Dropdown
              label="Vendor Type"
              placeholder="Select a vendor"
              options={vendorOptions}
              selectedKey={vendorType}
              onChange={handleVendorChange}
              required
              disabled={isLoading}
              styles={{ dropdown: { width: '100%' } }}
            />

            <Stack>
              <input
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={isLoading}
              />

              <DefaultButton
                iconProps={{ iconName: 'Upload' }}
                text={file ? file.name : 'Select CSV File'}
                onClick={openFileSelector}
                disabled={isLoading}
                styles={{ root: { width: '100%' } }}
              />
            </Stack>

            {uploadProgress > 0 && (
              <ProgressIndicator
                label="Uploading and parsing..."
                percentComplete={uploadProgress / 100}
                description={`${uploadProgress}% complete`}
              />
            )}

            <Stack horizontal tokens={{ childrenGap: 10 }}>
              <PrimaryButton
                text="Upload & Parse"
                iconProps={{ iconName: 'Upload' }}
                onClick={handleUpload}
                disabled={!file || !vendorType || isLoading}
              />

              <PrimaryButton
                text="Save to Database"
                iconProps={{ iconName: 'Save' }}
                onClick={handleSaveProducts}
                disabled={parsedProducts.length === 0 || isLoading}
                styles={{
                  root: {
                    backgroundColor: theme.palette.green,
                    borderColor: theme.palette.green,
                  },
                  rootHovered: {
                    backgroundColor: theme.palette.greenDark,
                    borderColor: theme.palette.greenDark,
                  },
                }}
              />
            </Stack>
          </Stack>
        </Stack.Item>

        <Stack.Item grow={2}>
          <Stack tokens={{ childrenGap: 12 }}>
            <Text variant="large">
              Parsed Products ({parsedProducts.length})
            </Text>

            <div
              style={{
                maxHeight: 400,
                overflow: 'auto',
                border: `1px solid ${theme.palette.neutralLight}`,
                borderRadius: 4,
              }}
            >
              {parsedProducts.length > 0 ? (
                <>
                  <DetailsList
                    items={displayedProducts}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    isHeaderVisible={true}
                    compact={true}
                  />
                  {remainingCount > 0 && (
                    <Stack
                      horizontal
                      horizontalAlign="center"
                      styles={{
                        root: {
                          padding: 12,
                          backgroundColor: theme.palette.neutralLighter,
                          borderTop: `1px solid ${theme.palette.neutralLight}`,
                        },
                      }}
                    >
                      <Text>... and {remainingCount} more product(s)</Text>
                    </Stack>
                  )}
                </>
              ) : (
                <Stack
                  horizontalAlign="center"
                  verticalAlign="center"
                  styles={{ root: { height: 150 } }}
                >
                  <Text style={{ color: theme.palette.neutralSecondary }}>
                    No products parsed yet. Upload a file to see results.
                  </Text>
                </Stack>
              )}
            </div>
          </Stack>
        </Stack.Item>
      </Stack>
    </Stack>
  );
};
